// @flow
import { formatPrice } from '../../utils/general';
import { useCallback, useContext, useState } from 'react';

import * as routes from '@src/app_routes/settings';

import { DiamondContext } from '@contexts/diamond';
import { SettingStateContext } from '@contexts/setting';

import Loading from '@components/icons/Loading';

import css from '@styles/productDetail/otherMetals';

type TListItem = {
  metal: string,
  humanized_metal: string,
  price: number,
  vendor_sku: string,
  url_segment_description: string
};

type TIconProps = {
  metal: string,
  humanizedMetal: string
};

const blackIcon = '/images/metal-color-icons/black.png';
const whiteIcon = '/images/metal-color-icons/white.png';
const yellowIcon = '/images/metal-color-icons/yellow.png';
const roseIcon = '/images/metal-color-icons/rose.png';

const metals = {
  '14KY': yellowIcon,
  '14KW': whiteIcon,
  '14KR': roseIcon,
  '14KY_14KW': yellowIcon,
  '14KY_14KR': yellowIcon,
  '14KW_14KY': whiteIcon,
  '14KW_14KR': whiteIcon,
  '14KR_14KY': roseIcon,
  '14KR_14KW': roseIcon,
  '18KY': yellowIcon,
  '18KW': whiteIcon,
  '18KR': roseIcon,
  '18KY_18KW': yellowIcon,
  '18KY_18KR': yellowIcon,
  '18KW_18KY': whiteIcon,
  '18KW_18KR': whiteIcon,
  '18KR_18KY': roseIcon,
  '18KR_18KW': roseIcon,
  PLAT: whiteIcon,
  TITANIUM: whiteIcon,
  BLACKTITANIUM: blackIcon
};

const metalOrder = [
  '14KW',
  '14KY',
  '14KR',
  '14KW_14KY',
  '14KW_14KR',
  '14KY_14KW',
  '14KY_14KR',
  '14KR_14KY',
  '14KR_14KW',
  '18KW',
  '18KY',
  '18KR',
  '18KW_18KY',
  '18KW_18KR',
  '18KY_18KW',
  '18KY_18KR',
  '18KR_18KY',
  '18KR_18KW',
  'PLAT',
  'TITANIUM',
  'BLACKTITANIUM'
];

function sortByMetalOrder (el1, el2) {
  return (
    metalOrder.findIndex((el) => el === el1.metal) -
    metalOrder.findIndex((el) => el === el2.metal)
  );
}

function OtherMetalIcon ({ metal, humanizedMetal }: TIconProps) {
  const icon = metals[metal];

  if (!icon) return null;

  return <img src={icon} alt={humanizedMetal} />;
}

function OtherMetalListItem ({
  item,
  onClick,
  priceDifference
}: {
  item: TListItem,
  onClick?: (sku: string) => void,
  priceDifference: number
}) {
  const [loading, setLoading] = useState(false);
  const handleClick = useCallback(
    (e) => {
      if (!onClick) return;

      e.preventDefault();
      setLoading(true);
      onClick(item.vendor_sku);
    },
    [onClick, item.vendor_sku]
  );

  return (
    <div className={css.listItem}>
      <div>
        {loading ? (
          <Loading
            style={{
              maxHeight: 12,
              maxWidth: 12,
              marginLeft: 3,
              marginRight: 13
            }}
          />
        ) : (
          <OtherMetalIcon
            metal={item.metal}
            humanizedMetal={item.humanized_metal}
          />
        )}
      </div>
      <div>
        <a href={routes.detailPath(item)} onClick={handleClick}>
          {item.humanized_metal}
        </a>
        <br />
        {priceDifference > 0 && `(+ ${formatPrice(Math.abs(priceDifference))})`}
        {priceDifference < 0 && `(- ${formatPrice(Math.abs(priceDifference))})`}
        {priceDifference === 0 && '(same price)'}
      </div>
    </div>
  );
}

function calculatePriceDifference (
  settingPrice: number,
  diamondPrice: ?number,
  itemPrice: number,
  includeDiamondInPriceDifference: boolean
) {
  let basePrice;
  let diffPrice;

  itemPrice = parseFloat(itemPrice);
  diamondPrice = parseFloat(diamondPrice);
  settingPrice = parseFloat(settingPrice);

  if (includeDiamondInPriceDifference) {
    basePrice = settingPrice + diamondPrice;
    diffPrice = itemPrice + diamondPrice;
  } else {
    basePrice = settingPrice;
    diffPrice = itemPrice;
  }

  // By rounding, we get the exact value that is displayed on PD pages. We want
  // to do the math based on those numbers, not the pre-rounded numbers. If we
  // do the math based on the pre-rounded numbers, we can be off by a few cents
  // which can result in us showing a difference that is off by one dollar after
  // rounding.
  basePrice = Math.round(basePrice);
  diffPrice = Math.round(diffPrice);

  return diffPrice - basePrice;
}

export default function OtherMetals ({
  onClick,
  includeDiamondInPriceDifference = false
}: {
  onClick?: (sku: string) => void,
  includeDiamondInPriceDifference?: boolean
}) {
  const { diamond } = useContext(DiamondContext) || { diamond: null };
  const { setting } = useContext(SettingStateContext);

  if (
    !setting.other_metals || // we may not have loaded extended data yet
    !setting.vendor_sku ||
    (includeDiamondInPriceDifference && !diamond)
  ) {
    return null;
  }

  const { other_metals: metals } = setting;

  if (metals.length === 0) return null;

  return (
    <div className={css.container}>
      <div className={css.title}>Also available in:</div>

      <div className={css.list}>
        {metals
          .sort(sortByMetalOrder)
          .map((item) => (
            <OtherMetalListItem
              item={item}
              priceDifference={calculatePriceDifference(
                setting.retail_price,
                diamond && diamond.retail_price,
                item.retail_price,
                includeDiamondInPriceDifference
              )}
              key={item.vendor_sku}
              onClick={onClick}
            />
          ))}
      </div>
    </div>
  );
}
